import {
  Button,
  Card,
  CardContent,
  CardHeader,
  DataList,
  DownloadButton,
  EmptyContent,
  Flex,
} from '@/components/ui';
import React, { useCallback, useState } from 'react';
import { FilePreviewModal } from '@/components/file-preview';
import { formatFileSize } from '@/utils/fileUtils';
import { usePermissionAccess } from '@/hooks/usePermissionAccess';
import { PERMISSIONS } from '@/types/permissions';
import { getWhitelistDownloadEndpoint } from '@/utils/downloadEndpoints';
import { IconEye, IconFileSearch } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { ActiveWhitelistCardProps } from './types';

export const ActiveWhitelistCard: React.FC<ActiveWhitelistCardProps> = ({
  activeWhitelist,
  onDownload,
}) => {
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const hasManageFilesPermission = usePermissionAccess([
    PERMISSIONS.STUDY_FILES_ACCESS,
  ]);

  const handlePreview = useCallback(() => {
    if (activeWhitelist && hasManageFilesPermission) {
      setPreviewModalOpen(true);
    }
  }, [activeWhitelist, hasManageFilesPermission]);

  const handleDownload = useCallback(() => {
    if (activeWhitelist) {
      onDownload(activeWhitelist);
    }
  }, [activeWhitelist, onDownload]);

  const handlePreviewModalChange = useCallback((open: boolean) => {
    setPreviewModalOpen(open);
  }, []);

  // Debug: Log the generated API endpoint
  const previewApiEndpoint = activeWhitelist
    ? getWhitelistDownloadEndpoint(activeWhitelist)
    : undefined;

  return (
    <Card disablePadding>
      <CardHeader
        cardTitle="Active Whitelist"
        description="Current file used for upload validation"
      />

      <CardContent className="active-whitelist-card__content">
        {activeWhitelist ? (
          <>
            <DataList.Root
              orientation="vertical"
              size="medium"
              style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}
            >
              <DataList.Item style={{ gridColumn: '-1 / 1' }}>
                <DataList.Label>Filename</DataList.Label>
                <DataList.Value>{activeWhitelist.filename}</DataList.Value>
              </DataList.Item>

              <DataList.Item>
                <DataList.Label>Size</DataList.Label>
                <DataList.Value>
                  {formatFileSize(activeWhitelist.size)}
                </DataList.Value>
              </DataList.Item>

              <DataList.Item>
                <DataList.Label>Uploaded By</DataList.Label>
                <DataList.Value>{activeWhitelist.uploadedBy}</DataList.Value>
              </DataList.Item>

              <DataList.Item>
                <DataList.Label>Upload Date</DataList.Label>
                <DataList.Value>
                  {dayjs(activeWhitelist.uploadDate).format(
                    'MMMM D, YYYY [at] h:mm A'
                  )}
                </DataList.Value>
              </DataList.Item>
            </DataList.Root>

            <Flex gap="2" mt="6">
              {hasManageFilesPermission && (
                <Button
                  onClick={handlePreview}
                  className="active-whitelist-card__preview-btn"
                  startIcon={<IconEye />}
                  color="secondary"
                  variant="surface"
                  size="small"
                  style={{ flex: 1 }}
                >
                  Preview
                </Button>
              )}

              <DownloadButton
                apiEndpoint={getWhitelistDownloadEndpoint(activeWhitelist)}
                filename={activeWhitelist.filename}
                variant="outlined"
                color="secondary"
                size="small"
                style={{ flex: 1 }}
                successMessage={`Downloaded ${activeWhitelist.filename}`}
                errorMessage="Failed to download file"
              >
                Download
              </DownloadButton>
            </Flex>
          </>
        ) : (
          <EmptyContent
            size="small"
            icon={<IconFileSearch />}
            title="No active whitelist"
            description="Upload a file to activate a whitelist"
          />
        )}
      </CardContent>

      {/* File Preview Modal */}
      <FilePreviewModal
        key={`preview-${activeWhitelist?.id || 'none'}`}
        apiEndpoint={previewApiEndpoint}
        filename={activeWhitelist?.filename || ''}
        mimeType="text/csv"
        fileSize={activeWhitelist?.size}
        open={previewModalOpen && !!activeWhitelist}
        onOpenChange={handlePreviewModalChange}
        title={
          activeWhitelist ? `Preview: ${activeWhitelist.filename}` : 'Preview'
        }
        onDownload={handleDownload}
      />
    </Card>
  );
};
